<template>
  <n-modal
    :show="show"
    preset="card"
    class="switch-assembly-child-node-version-dialog"
    :style="dialogThemeVars"
    :auto-focus="false"
    tabindex="-1"
    title="切换到历史提交"
    :bordered="false"
    @close="emit('cancel')"
    @after-enter="focusNaiveModalCardAfterEnter"
    @mask-click="emit('cancel')"
    @esc="emit('cancel')"
  >
    <div class="switch-assembly-child-node-version-dialog__body">
      <div
        v-if="node"
        class="switch-assembly-child-node-version-dialog__node"
      >
        <div class="switch-assembly-child-node-version-dialog__node-row">
          <span class="switch-assembly-child-node-version-dialog__node-label">节点</span>
          <strong class="switch-assembly-child-node-version-dialog__node-name">{{ node.name }}</strong>
        </div>
        <div class="switch-assembly-child-node-version-dialog__node-row">
          <span class="switch-assembly-child-node-version-dialog__node-label">当前版本</span>
          <strong class="switch-assembly-child-node-version-dialog__node-name">{{ node.adoptedVersionLabel }}</strong>
        </div>
      </div>

      <n-empty
        v-if="!node"
        description="请选择目标节点"
        size="small"
      />
      <n-spin v-else :show="loading" size="small">
        <n-empty
          v-if="commits.length === 0"
          description="暂无提交记录"
          size="small"
        />
        <div
          v-else
          class="switch-assembly-child-node-version-dialog__list"
        >
          <button
            v-for="commit in commits"
            :key="commit.id"
            type="button"
            class="switch-assembly-child-node-version-dialog__commit"
            :class="{ 'switch-assembly-child-node-version-dialog__commit--adopted': commit.id === adoptedCommitId }"
            :data-commit-id="commit.id"
            :disabled="loading || commit.id === adoptedCommitId"
            @click="emit('selectCommit', commit.id)"
          >
            <span class="switch-assembly-child-node-version-dialog__commit-main">
              <span class="switch-assembly-child-node-version-dialog__message">{{ commit.message }}</span>
            </span>
            <span class="switch-assembly-child-node-version-dialog__meta">
              <span>{{ commit.branchName }}</span>
              <span>{{ commit.authorName }}</span>
              <span>{{ formatCommitTime(commit.committedAt) }}</span>
            </span>
            <span class="switch-assembly-child-node-version-dialog__action">
              <n-tag
                v-if="commit.id === adoptedCommitId"
                class="switch-assembly-child-node-version-dialog__adopted-tag"
                size="small"
                type="success"
                round
              >
                采纳版本
              </n-tag>
              <span
                v-else
                class="switch-assembly-child-node-version-dialog__select-label"
              >
                切换到此版本
              </span>
            </span>
          </button>
        </div>
      </n-spin>
    </div>
  </n-modal>
</template>

<script setup lang="ts">
/**
 * 子节点采纳版本切换弹窗。
 *
 * 列表只标识当前采纳版本，不展示“新提交”语义，避免和正式提交、分支状态混淆。
 */
import { computed } from 'vue';
import type { CSSProperties } from 'vue';
import { NEmpty, NModal, NSpin, NTag } from 'naive-ui';
import { focusNaiveModalCardAfterEnter } from '@/hooks/useNaiveModalCardFocus';
import type { AssemblyChildNodeCommit, AssemblyChildNodeSummary } from '@/services/assembly-branch-node-list';
import { useGlobalConfigStore } from '@/stores';

defineProps<{
  /** 是否显示弹窗。 */
  show: boolean;
  /** 当前准备切换版本的子节点。 */
  node: AssemblyChildNodeSummary | null;
  /** 当前节点的提交列表。 */
  commits: readonly AssemblyChildNodeCommit[];
  /** 当前父装配体采纳的提交 ID。 */
  adoptedCommitId: string | null;
  /** 提交列表或切换动作是否正在加载。 */
  loading: boolean;
}>();

const emit = defineEmits<{
  /** 选择目标提交作为采纳版本。 */
  selectCommit: [commitId: string];
  /** 取消并关闭弹窗。 */
  cancel: [];
}>();

type SwitchDialogThemeVars = CSSProperties & Record<string, string>;

const globalConfigStore = useGlobalConfigStore();
const isDarkTheme = computed(() => Boolean(globalConfigStore.getProviderTheme()));

/**
 * 为历史提交切换弹框提供稳定的主题色变量。
 *
 * Naive Modal 会被传送到 body 下，局部样式里直接依赖内部主题变量时，
 * 标题与元信息容易被可点击元素的主色污染；这里在弹框根节点显式注入语义色。
 */
const dialogThemeVars = computed<SwitchDialogThemeVars>(() => {
  if (!isDarkTheme.value) {
    return {
      width: 'min(540px, calc(100vw - 32px))',
      '--assembly-dialog-title-color': '#1f2937',
      '--assembly-dialog-meta-color': '#7a8795',
      '--assembly-dialog-action-color': '#2c75b8',
      '--assembly-dialog-row-surface': '#f7f9fc',
      '--assembly-dialog-hover-surface': '#f1f6fb',
      '--assembly-dialog-adopted-surface': '#fbfdff',
      '--assembly-dialog-adopted-border': '#2d8cf0',
    };
  }

  return {
    width: 'min(540px, calc(100vw - 32px))',
    '--assembly-dialog-title-color': '#f3f6fa',
    '--assembly-dialog-meta-color': '#8f9aa8',
    '--assembly-dialog-action-color': '#84b8e8',
    '--assembly-dialog-row-surface': '#30343b',
    '--assembly-dialog-hover-surface': '#343b45',
    '--assembly-dialog-adopted-surface': '#2d323a',
    '--assembly-dialog-adopted-border': '#5aa4ef',
  };
});

/**
 * 格式化提交时间。
 *
 * @param committedAt ISO 格式提交时间。
 * @returns YYYY-MM-DD HH:mm 格式时间。
 */
function formatCommitTime(committedAt: string): string {
  const matched = committedAt.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  if (matched) {
    return `${matched[1]} ${matched[2]}`;
  }
  return committedAt;
}
</script>

<style scoped>
.switch-assembly-child-node-version-dialog {
  width: 540px;
  --assembly-dialog-surface: var(--n-card-color, var(--n-modal-color, var(--n-color)));
  --assembly-dialog-soft-surface: color-mix(in srgb, var(--assembly-dialog-surface) 90%, var(--n-text-color) 4%);
  --assembly-dialog-row-surface: color-mix(in srgb, var(--assembly-dialog-surface) 84%, var(--n-primary-color) 10%);
  --assembly-dialog-hover-surface: color-mix(in srgb, var(--assembly-dialog-surface) 78%, var(--n-primary-color) 16%);
  --assembly-dialog-adopted-surface: color-mix(in srgb, var(--assembly-dialog-surface) 82%, var(--n-primary-color) 12%);
  --assembly-dialog-border-soft: color-mix(in srgb, var(--n-border-color) 70%, transparent);
}

.switch-assembly-child-node-version-dialog:focus {
  outline: none;
}

.switch-assembly-child-node-version-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.switch-assembly-child-node-version-dialog__node {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 8px;
  padding: 2px 0 14px;
  border-bottom: 1px solid var(--assembly-dialog-border-soft);
}

.switch-assembly-child-node-version-dialog__node-row {
  display: flex;
  min-width: 0;
  gap: 10px;
  align-items: baseline;
}

.switch-assembly-child-node-version-dialog__node-label {
  flex: 0 0 58px;
  color: var(--n-text-color-2);
  font-size: 12px;
}

.switch-assembly-child-node-version-dialog__node-name {
  min-width: 0;
  overflow: hidden;
  color: var(--n-text-color);
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switch-assembly-child-node-version-dialog__list {
  display: flex;
  max-height: min(420px, 52vh);
  flex-direction: column;
  gap: 9px;
  overscroll-behavior: contain;
  overflow-y: auto;
  padding: 2px 2px 2px 0;
  scrollbar-gutter: stable;
}

.switch-assembly-child-node-version-dialog__commit {
  display: grid;
  width: 100%;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 7px 14px;
  align-items: center;
  padding: 13px 14px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: var(--assembly-dialog-row-surface);
  color: var(--n-text-color);
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.16s ease,
    background 0.16s ease;
}

.switch-assembly-child-node-version-dialog__commit:not(:disabled):hover {
  border-color: var(--n-primary-color);
  background: var(--assembly-dialog-hover-surface);
}

.switch-assembly-child-node-version-dialog__commit:disabled {
  cursor: default;
}

.switch-assembly-child-node-version-dialog__commit--adopted {
  border-color: color-mix(in srgb, var(--n-primary-color) 34%, transparent);
  background: var(--assembly-dialog-adopted-surface);
}

.switch-assembly-child-node-version-dialog__commit-main {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
}

.switch-assembly-child-node-version-dialog__message {
  min-width: 0;
  overflow: hidden;
  color: var(--n-text-color);
  font-size: 13px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switch-assembly-child-node-version-dialog__meta {
  display: flex;
  min-width: 0;
  gap: 7px;
  grid-column: 1;
  overflow: hidden;
  color: var(--n-text-color-2);
  font-size: 12px;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.switch-assembly-child-node-version-dialog__meta span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.switch-assembly-child-node-version-dialog__meta span:not(:first-child)::before {
  margin-right: 7px;
  color: var(--n-text-color-disabled);
  content: '/';
}

.switch-assembly-child-node-version-dialog__action {
  display: flex;
  grid-row: 1 / span 2;
  grid-column: 2;
  align-items: center;
  justify-content: flex-end;
}

.switch-assembly-child-node-version-dialog__adopted-tag {
  flex: 0 0 auto;
  --n-color: color-mix(in srgb, var(--n-primary-color) 18%, transparent);
  --n-border: 1px solid color-mix(in srgb, var(--n-primary-color) 36%, transparent);
  --n-text-color: var(--n-primary-color);
}

.switch-assembly-child-node-version-dialog__select-label {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 10px;
  border: 1px solid color-mix(in srgb, var(--n-primary-color) 38%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, var(--n-primary-color) 10%, transparent);
  color: var(--n-primary-color);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.switch-assembly-child-node-version-dialog__commit:not(:disabled):hover .switch-assembly-child-node-version-dialog__select-label {
  background: color-mix(in srgb, var(--n-primary-color) 18%, transparent);
}
</style>
